import { HttpErrorResponse } from '@angular/common/http';

import {
  createSelector,
  createSelectorFactory,
  defaultMemoize,
  MemoizedSelector,
} from '@ngrx/store';

import { UserSelectors } from '@recruitee/account/data';
import { Ability, Candidate, CandidateRating, Placement, User } from '@recruitee/http/types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  PickActions,
  pipeState,
  Reference,
} from '@recruitee/ngrx';
import { PlacementSelectors } from '@recruitee/placements/data';

import { EntityStoreKey } from './../types';
import { CandidateCollectionParams } from './candidate-http-client';

const idSelector = (candidate: { id: number }) => candidate.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Candidate>()(
  EntityStoreKey.candidate,
  {
    idSelector,
    references: makeRef =>
      <Array<Reference<Candidate>>>[
        makeRef(
          'placements',
          'placements',
          (placements: Placement[]): ((store: any) => Placement[]) =>
            PlacementSelectors.byIdsWithReferences((placements || []).map(({ id }) => id)),
        ),
      ],
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        references: (state, payload: { data: Candidate[] }) =>
          adapter.updateOrCreateMany(state, payload.data),

        fetchCollection: (
          state,
          {
            scope,
            params,
            clear,
          }: {
            scope: string;
            params: CandidateCollectionParams;
            clear?: boolean;
          },
        ) =>
          pipeState(
            state,
            next => (clear ? adapter.resetScope(next, scope) : next),
            next => defaultActions.fetchCollection(next, { scope }),
          ),

        fetchCandidateRating: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        fetchCandidateRatingSuccess: (state, payload: CandidateRating) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload),
            next => adapter.markAsLoaded(next, { entities: [payload.id], scope: null }),
          ),
        fetchCandidateRatingFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const byIds = memoizeSelector(
  (candidateIds: number[]): MemoizedSelector<any, any> =>
    createSelectorFactory(defaultMemoize)(
      ...candidateIds.map(candidateId => selectors.byId(candidateId)!),
      (...result: Candidate[]) => result,
    ),
);

// Tagging fns

const taggedAdmins = createSelector(UserSelectors.entities(), users =>
  users.map(user => ({ ...user, hasCandidateAccess: false })),
);
const tagAdminsWithManageHiredCandidates = (users: User[]) =>
  users.map(user => ({
    ...user,
    hasCandidateAccess: !user.roleAbilities.includes(Ability.ManageHiredCandidates),
  }));
const tagAdminsWithCandidateId = (users: User[], id?: number) =>
  id
    ? users.map(user => ({ ...user, hasCandidateAccess: !user.candidateIds?.includes(id) }))
    : users;
const tagAdminsWithAccessBasedOnHiringStatus = (users: User[], candidate: Candidate | null) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagAdminsWithManageHiredCandidates(users)
    : tagAdminsWithCandidateId(users, candidate?.id);

const tagAdminsWithAccessToOfferOrCandidate = (
  users: User[],
  candidateId: number | undefined,
  offerId: number,
) =>
  users.map(user => ({
    ...user,
    hasCandidateAccess: !(
      user.viewAllJobs ||
      user.offerIds?.includes(offerId) ||
      (candidateId && user.candidateIds?.includes(candidateId))
    ),
  }));

const tagAdminsForOffer = (users: User[], candidate: Candidate | null, offerId: number) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagAdminsWithManageHiredCandidates(users)
    : tagAdminsWithAccessToOfferOrCandidate(users, candidate?.id, offerId);

const tasksAdminsTagged = memoizeSelector((candidateId: number) =>
  createSelector(taggedAdmins, selectors.byIdWithReferences(candidateId), (users, candidate) =>
    tagAdminsWithAccessBasedOnHiringStatus(users, candidate),
  ),
);

const requestEvaluationAdminsTagged = memoizeSelector(
  (props: { candidateId: number; offerId?: number }) =>
    createSelector(
      UserSelectors.entities(),
      selectors.byIdWithReferences(props.candidateId),
      (users, candidate) =>
        props.offerId
          ? tagAdminsForOffer(users, candidate, props.offerId)
          : tagAdminsWithAccessBasedOnHiringStatus(users, candidate),
    ),
);

const filterTaggedUsers = (users: User[], hasCandidateAccess: boolean) =>
  users.filter(user => user.hasCandidateAccess === hasCandidateAccess);

// Selectors for tagged admins

const tasksAdminsWithAccess = memoizeSelector((candidateId: number) =>
  createSelector(tasksAdminsTagged(candidateId), users => filterTaggedUsers(users, false)),
);
const tasksAdminsWithoutAccess = memoizeSelector((candidateId: number) =>
  createSelector(tasksAdminsTagged(candidateId), users => filterTaggedUsers(users, true)),
);

const requestEvaluationAdminsWithAccess = memoizeSelector(
  (props: { candidateId: number; offerId?: number }) =>
    createSelector(requestEvaluationAdminsTagged(props), users => filterTaggedUsers(users, false)),
);

const requestEvaluationAdminsWithoutAccess = memoizeSelector(
  (props: { candidateId: number; offerId?: number }) =>
    createSelector(requestEvaluationAdminsTagged(props), users => filterTaggedUsers(users, true)),
);

const CandidateActions: Pick<typeof actions, 'references'> &
  PickActions<typeof actions, 'fetchCollection' | 'fetchCandidateRating'> = actions;

const CandidateSelectors = {
  ...selectors,
  byIds,
  tasksAdminsWithAccess,
  tasksAdminsWithoutAccess,
  requestEvaluationAdminsWithAccess,
  requestEvaluationAdminsWithoutAccess,
};

export {
  CandidateActions,
  CandidateSelectors,
  namespace as candidateNamespace,
  reducer as CandidateReducer,
};
